import React from 'react';
import { enableBodyScroll } from 'body-scroll-lock';
import { switchTabAction } from 'containers/Header/actions';
import TabNames from 'containers/Header/TabNames';
import { disableScroll, enableScroll } from '../../utils/disableScroll';
import { displayTutorialAction } from './actions';
import { Trans } from 'react-i18next';
import { dragElement } from './drag';

const enableBody = (target: HTMLDivElement) => enableBodyScroll(target);

export const onBeforeCloseTutorial = (target: HTMLDivElement) => {
  enableScroll();
  removeActiveTutorialStep();
};

const onStartStep = () => {
  const element_to_scroll_to = document.getElementById('kewconf');
  if (element_to_scroll_to) element_to_scroll_to.scrollIntoView();
  disableScroll();
};

export const removeActiveTutorialStep = () => {
  for (const item of document.querySelectorAll('.active-tuto-step'))
    item.classList.remove('active-tuto-step');
};

type PositionTuto = 'right' | 'left' | 'top' | 'bottom' | 'center' | undefined;

function highlightStep(node: HTMLDivElement | undefined) {
  const nodeTutos = document.getElementsByClassName('tuto-message');
  if (nodeTutos.length > 0) {
    removeActiveTutorialStep();
    onStartStep();

    if (node) {
      node.className = `${node.className} active-tuto-step`;
      dragElement(document.querySelector('.tuto-message'));
    }
  }
}

function stepAction(
  stepNumber: number,
  activeTab: TabNames,
  stepTab: TabNames,
  dispatch: any,
  node: HTMLDivElement | undefined,
) {
  if (activeTab !== stepTab) dispatch(switchTabAction(stepTab, stepNumber));
  else highlightStep(node);
}

export const tutorialSteps = (dispatch: any, activeTab: TabNames, t?: any) => [
  {
    selector: '.tuto-step-cabinet-dimensions',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-cabinet-dimensions">
          Choose the <strong>width</strong> of the cabinet from 25 to 120 cm,
          then choose the <strong>depth</strong> from 25 to 62 cm. The 3D view
          and the price are imediately updated.
        </Trans>
      </div>
    ),
    position: 'right' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(0, activeTab, TabNames.cabinet, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-cabinet-corners',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-cabinet-corners">
          Click on a colour to choose the cabinet's metal
          <strong>corners</strong>.
        </Trans>
      </div>
    ),
    position: 'left' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(1, activeTab, TabNames.cabinet, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-cabinet-support',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-cabinet-support">
          Click on this menu to choose <strong>feets</strong>,
          <strong>wheels</strong> or <strong>suspension</strong>.
        </Trans>
      </div>
    ),
    position: 'left' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(2, activeTab, TabNames.cabinet, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-tab',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-tab">
          The compartments of the cabinet can be colour-coded and customized.
          Click on a compartment in the 3D view to select it or click on the
          <strong>Compartments</strong> tab to select all your compartments.
        </Trans>
      </div>
    ),
    position: 'bottom' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(3, activeTab, TabNames.cabinet, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-selection',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-selection">
          With this button you can <strong>add more compartments</strong> to
          your cabinet. A cabinet can be as tall as 3.5 m.
        </Trans>
      </div>
    ),
    position: 'right' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(4, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-height',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-height">
          Choose the <strong>height</strong> of the compartment, from 10 to 139
          cm. The large compartment can be used as a <i>wardrobe</i> with an
          included rod to hang your clothes.
        </Trans>
      </div>
    ),
    position: 'right' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(5, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-composition',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-composition">
          Choose the composition of the compartment. A compartment can be
          <i>open</i>, have sliding <b>doors</b> at the front, doors on front
          and back, or a set of <b>drawers</b>.
        </Trans>
      </div>
    ),
    position: 'right' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(6, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-parts',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-parts">
          Here you can customize the compartment components and <b>color</b>
          them.
        </Trans>
      </div>
    ),
    position: 'left' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(7, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-shelves-accessories',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-shelves-accessories">
          You can here change door or drawer <strong>handles</strong>. You can
          also add one or more <strong>intermediate shelves</strong> and
          customize them.
        </Trans>
      </div>
    ),
    position: 'left' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(8, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '.tuto-step-save',
    content: ({ goTo, inDOM }: any) => (
      <div>
        <Trans i18nKey="tuto-step-save">
          To finish, don't forget to <strong>save</strong> the cabinet to get a
          copy by email or <strong>add it to the cart</strong> to order it.
        </Trans>
      </div>
    ),
    position: 'left' as PositionTuto,
    action: (node: HTMLDivElement | undefined) => {
      stepAction(8, activeTab, TabNames.shelves, dispatch, node);
    },
  },
  {
    selector: '',
    content: ({ goTo, inDOM }: any) => (
      <div>
        {t('If you need help, please do not hesitate to call us at')}{' '}
        <b>
          <a className="whitespace-no-wrap" href="tel:+3280014895">
            +32 (0) 800 148 95
          </a>
        </b>{' '}
        {t('or send us an email at')}{' '}
        <b>
          <a href="mailto:info@kewlox.com">info@kewlox.com</a>
        </b>
        .<br />
        <br />
        <a
          className="whitespace-no-wrap underline"
          href="#"
          onClick={() => goTo(0)}
        >
          {t('Start the wizard again')}
        </a>
      </div>
    ),
    action: (node: HTMLDivElement | undefined) => {
      stepAction(8, TabNames.shelves, TabNames.shelves, dispatch, undefined);
    },
  },
];
